// Tipos del Módulo de Diseño (M_05.14)

export type DesignStatus =
  | 'request' | 'inputs' | 'in_design' | 'in_review' | 'validation' | 'measurement' | 'closed' | 'cancelled';

export const DESIGN_STATUS_LABELS: Record<DesignStatus, string> = {
  request: 'Solicitud',
  inputs: 'Entradas',
  in_design: 'En diseño',
  in_review: 'En revisión',
  validation: 'Validación',
  measurement: 'Mediciones',
  closed: 'Cerrado',
  cancelled: 'Cancelado',
};

export const DESIGN_STATUS_COLORS: Record<DesignStatus, string> = {
  request: 'bg-slate-500/10 text-slate-600 border-slate-500/20',
  inputs: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  in_design: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20',
  in_review: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  validation: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
  measurement: 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20',
  closed: 'bg-green-500/10 text-green-600 border-green-500/20',
  cancelled: 'bg-red-500/10 text-red-600 border-red-500/20',
};

// Y del PN: 1 Pieza, 2 Master, 3 Molde, 4 Utillaje
export const PN_TYPE_LABELS: Record<number, string> = {
  1: 'Pieza',
  2: 'Master',
  3: 'Molde',
  4: 'Utillaje',
};

export interface DesignInputs {
  herramientas?: string;
  material_disponible?: string;
  especificaciones_pieza?: string;
  especificaciones_master?: string;
  especificaciones_molde?: string;
  especificaciones_utillaje?: string;
  legales?: string;
  normativas?: string;
  items_criticos_diseno?: string;
  items_criticos_fabricacion?: string;
  amfe?: string;
  // URLs de documento (PDF/enlace) para especificaciones complejas
  especificaciones_pieza_url?: string;
  especificaciones_master_url?: string;
  especificaciones_molde_url?: string;
  especificaciones_utillaje_url?: string;
}

// Texto por defecto de herramientas disponibles (editable por proyecto)
export const DEFAULT_DESIGN_TOOLS = `MasterCAM 2025
TopSolid 7.11
Scanner Laser FreeScan Combo
Geomagic Wrap
Herramientas de medición disponibles en Magma Composites`;

export interface DesignMeasurement {
  status?: 'pendiente' | 'ok' | 'nok';
  notes?: string;
}
export interface DesignMeasurements {
  master?: DesignMeasurement;
  molde?: DesignMeasurement;
  pieza?: DesignMeasurement;
}

export interface DesignProject {
  id: string;
  code: string | null;
  name: string;
  customer_id: string | null;
  project_id: string | null;
  request_summary: string | null;
  owner: string | null;
  solution_provider: string | null;
  director_id: string | null;
  design_lead_id: string | null;
  status: DesignStatus;
  start_date: string | null;
  end_date: string | null;
  schedule_notes: string | null;
  inputs: DesignInputs | null;
  measurements?: DesignMeasurements | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DesignRevision {
  id: string;
  design_project_id: string;
  revision_number: number;
  revision_date: string | null;
  changes: string | null;
  responsible_id: string | null;
  status: 'draft' | 'approved';
  created_at: string;
}

export type MachiningStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export const MACHINING_STATUS_LABELS: Record<MachiningStatus, string> = {
  pending: 'Pendiente',
  in_progress: 'En progreso',
  completed: 'Completada',
  cancelled: 'Cancelada',
};

export const MACHINING_STATUS_COLORS: Record<MachiningStatus, string> = {
  pending: 'bg-slate-500/10 text-slate-600 border-slate-500/20',
  in_progress: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  completed: 'bg-green-500/10 text-green-600 border-green-500/20',
  cancelled: 'bg-red-500/10 text-red-600 border-red-500/20',
};

export interface MachiningOrder {
  id: string;
  code: string | null;
  design_project_id: string | null;
  design_part_number_id: string | null;
  machine_id: string | null;
  material_source: 'raw_materials' | 'material_inventory' | 'none';
  material_id: string | null;
  material_name: string | null;
  material_qty: number | null;
  material_unit: string | null;
  status: MachiningStatus;
  operator_id: string | null;
  started_at: string | null;
  finished_at: string | null;
  stock_discounted: boolean;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface MachiningOperation {
  id: string;
  machining_order_id: string;
  sequence: number;
  program: string | null;
  tool_code: string | null;
  tool_offset: string | null;
  holder_code: string | null;
  tool_number: string | null;
  duration: string | null;
  observations: string | null;
  created_at: string;
}

export interface DesignPartNumber {
  id: string;
  design_project_id: string;
  pn_code: string;
  pn_type: number; // 1..4
  client_part_pn: string | null;
  subassembly: string | null;
  num_z: string | null;
  num_w: string | null;
  version: string | null;
  name: string | null;
  photo_url: string | null;
  validated: boolean;
  validated_at: string | null;
  validated_by: string | null;
  linked_mold_id: string | null;
  linked_tool_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}
